import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { exchangeProxyCode } from '../services/api/testOauthProxy';
import { sendGoogleLogin } from '../services/api/login';
import { setUserInfo, setAuthToken, setLabPartnerInfo } from '../actions';
import config from '../config';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LoaderRoot = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at top, #f5f8ff 0%, #ffffff 60%);
  font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 40px 56px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(60, 64, 67, 0.12), 0 2px 6px rgba(60, 64, 67, 0.08);
  animation: ${fadeIn} 0.35s ease-out;
`;

const SpinnerWrap = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
`;

const SpinnerRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 4px solid transparent;
  border-top-color: #4285f4;
  border-right-color: #ea4335;
  border-bottom-color: #fbbc05;
  border-left-color: #34a853;
  animation: ${spin} 1s linear infinite;
`;

const SpinnerInner = styled.div`
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  background: #ffffff;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #202124;
  letter-spacing: 0.15px;
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: #5f6368;
  letter-spacing: 0.2px;
`;

// One-time codes from the on-demand SSO proxy are single-use on the server.
// Track which we've already exchanged in this tab so any re-mount of this
// page (StrictMode, HMR, transient parent re-render) does not replay the call.
const consumedCodes = new Set();

// Named export used in tests to bypass withRouter/connect HOC wrappers
export class TestOauthReturn extends Component {
  async componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code || consumedCodes.has(code)) {
      this.props.history.push('/');
      return;
    }
    consumedCodes.add(code);

    // Drop the code from the URL so a manual reload of /auth/return cannot
    // replay the exchange and trip the server's single-use guard.
    const pathname = (window.location && window.location.pathname) || '/auth/return';
    if (window.history && typeof window.history.replaceState === 'function') {
      window.history.replaceState({}, '', pathname);
    }

    try {
      const proxyBaseUrl = 'https://api.getvisitapp.com/v3';
      const { idToken } = await exchangeProxyCode(code, proxyBaseUrl);
      const response = await sendGoogleLogin(idToken);

      if (!response || !response.body || response.body.message !== 'success') {
        throw new Error('Login response was not successful');
      }

      // Load the operator's permission keys (showOps/showLabs/showSupport/…)
      // into the user reducer so the sidebar tabs map. The proxy flow used on
      // non-prod previously dispatched only the token, leaving these keys at
      // their default `false`, which is why no nav tabs showed up on stage.
      // This mirrors the prod googleLogin flow in components/login/login.js.
      this.props.dispatch(setUserInfo(response.body.result));
      this.props.dispatch(setAuthToken(response.headers.authorization));
      if (response.body.partnerInfo) {
        this.props.dispatch(setLabPartnerInfo(response.body.partnerInfo));
      }

      this.props.history.push('/revamp-offline-consults');
    } catch (err) {
      console.error('Test OAuth return failed:', err);
      this.props.history.push('/');
    }
  }

  render() {
    return (
      <LoaderRoot>
        <Card>
          <SpinnerWrap>
            <SpinnerRing />
            <SpinnerInner />
          </SpinnerWrap>
          <Title>Signing you in</Title>
          <Subtitle>Completing authentication, please wait...</Subtitle>
        </Card>
      </LoaderRoot>
    );
  }
}

export default withRouter(connect()(TestOauthReturn));
